import React, { useState } from "react";
import { ArrowLeft, Check, Play, Layers, Plus, Link2, Copy, Clock, ChevronDown, ChevronRight } from "lucide-react";
import { Spinner, buildUniversalExtractUrl, copyTextToClipboard, LLM_MODEL_OPTIONS, RETRIEVAL_OPTIONS } from "../../constants";
import { ENGINE_OPTIONS } from "../../OcrEngineModal";
import { docTypeStyle } from "../studioConst";
import { SX, FRAME, TX } from "../studioTokens";
import ApiAccessMenu from "./ApiUsage";
import FieldEditor from "./FieldEditor";
import { buildFieldHistory } from "./fieldHistory";

const MetaCell = ({ label, children }) => (
  <div>
    <div className="text-[10.5px] font-semibold tracking-[0.06em] text-[#8A8A90] uppercase mb-[7px]">{label}</div>
    {children}
  </div>
);

/* 04 · Template View — the read/edit surface for one saved template's schema. */
const TemplateDetail = ({
  template, templateName, onNameChange, slug, onSlugChange,
  config, onConfigChange, dirty, saving,
  onAddField, onRename, onChangeType, onChangeInstruction, onChangeSchema, onDelete, onToggleEnable,
  onSave, onStartTest, onBack,
}) => {
  const ts = docTypeStyle(template.document_type);
  const fields = template.fields || [];
  const [copied, setCopied] = useState(false);
  const [apiOpen, setApiOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const rounds = (template.refinement_history || []).slice().reverse();

  const endpoint = slug ? buildUniversalExtractUrl(slug) : null;
  const copyEndpoint = () => {
    if (!endpoint) return;
    copyTextToClipboard(endpoint).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };

  const selectStyle = `${SX.input} !py-2 !text-[13px] font-semibold cursor-pointer`;

  return (
    <div className={`flex flex-col flex-1 min-h-0 font-outfit ${FRAME.canvas}`} onClick={() => apiOpen && setApiOpen(false)}>
      {/* Top bar */}
      <div className={`${SX.topbar} gap-3.5`}>
        <button onClick={onBack} title="Back"
          className="w-9 h-9 flex items-center justify-center rounded-lg text-[#1A1A1A] hover:bg-[#F5F5F5] transition-colors flex-shrink-0">
          <ArrowLeft size={20} />
        </button>
        <div className={`w-px h-6 ${SX.divider}`} />
        <input value={templateName} onChange={(e) => onNameChange(e.target.value)} placeholder="Untitled template"
          className="text-[16.5px] font-semibold text-[#1A1A1A] border border-transparent rounded-lg px-2 py-1 bg-transparent min-w-[180px] placeholder:text-[#8A8A90] hover:border-[#ECE9EE] focus:border-[#A04A97] focus:bg-white focus:shadow-[0_0_0_3px_rgba(160,74,151,0.14)] outline-none transition-all" />
        <div className="flex-1" />
        {dirty && (
          <button onClick={onSave} disabled={saving}
            className={`${SX.btn} bg-[#2F7D44] text-white border-none shadow-[0_2px_6px_rgba(0,0,0,0.08)] hover:enabled:bg-[#276437]`}>
            {saving ? <><Spinner size={14} /> Saving…</> : <><Check size={14} /> Save changes</>}
          </button>
        )}
        <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button className={`${SX.btn} ${SX.btnGhost}`} onClick={() => setApiOpen((v) => !v)}>
            <Link2 size={14} /> API Access <ChevronDown size={13} className="opacity-60" />
          </button>
          {apiOpen && <ApiAccessMenu slug={slug} />}
        </div>
        <button onClick={onStartTest} className={`${SX.btn} ${SX.btnPrimary}`}>
          <Play size={14} fill="currentColor" /> Start Test
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        <div className={FRAME.templateView}>
          {/* Meta card */}
          <div className={`${SX.card} px-[22px] py-5 mb-[22px]`}>
            <div className="grid grid-cols-4 gap-5 items-start">
              <MetaCell label="Document Type">
                <span className={`${SX.badge} !text-[11.5px] ${ts.badge}`}>{ts.label}</span>
              </MetaCell>
              <MetaCell label="Template Slug">
                {slug ? (
                  <input value={slug} onChange={(e) => onSlugChange(e.target.value)}
                    className={`${SX.input} !py-2 !text-[12.5px] ${SX.inputMono}`} />
                ) : (
                  <span className="inline-block text-[12.5px] text-[#8A8A90] pt-2">Set on first save</span>
                )}
              </MetaCell>
              <MetaCell label="Fields">
                <span className="inline-block text-[13.5px] font-semibold text-[#1A1A1A] pt-[7px]">{fields.length} configured</span>
              </MetaCell>
              <MetaCell label="Extraction Engine">
                <select value={config.engine} onChange={(e) => onConfigChange("engine", e.target.value)} className={selectStyle}>
                  {ENGINE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </MetaCell>
              <MetaCell label="AI Model">
                <select value={config.model} onChange={(e) => onConfigChange("model", e.target.value)} className={selectStyle}>
                  {LLM_MODEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </MetaCell>
              <MetaCell label="Retrieval Mode">
                <select value={config.retrieval} onChange={(e) => onConfigChange("retrieval", e.target.value)} className={selectStyle}>
                  {RETRIEVAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </MetaCell>
            </div>

            <div className={`h-px my-[18px] ${SX.divider}`} />
            <div className="text-[10.5px] font-semibold tracking-[0.06em] text-[#8A8A90] uppercase mb-2">Extraction Endpoint</div>
            <div className="flex items-center gap-2.5 bg-[#FAF9FB] border border-[#ECE9EE] rounded-[9px] px-3.5 py-2.5">
              <Link2 size={13} className="text-[#8A8A90] flex-shrink-0" />
              <span className="flex-1 font-mono text-[12.5px] text-[#52525B] truncate">
                {endpoint || "Save the template to mint its extraction endpoint."}
              </span>
              {endpoint && (
                <button onClick={copyEndpoint} className={`${SX.btn} ${SX.btnGhost} ${SX.btnSm} flex-shrink-0`}>
                  {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                </button>
              )}
            </div>
          </div>

          {/* Fields */}
          <div className={`${SX.card} overflow-hidden`}>
            <div className="flex items-center justify-between px-[22px] py-4 bg-[#FBF5FA] border-b border-[#ECE9EE]">
              <div className="flex items-center gap-2.5">
                <Layers size={16} className="text-[#1A1A1A]" />
                <span className="text-[13px] font-semibold tracking-[0.04em] uppercase text-[#1A1A1A]">Extraction Fields</span>
                <span className="px-2.5 py-0.5 rounded-full bg-white border border-[#E9D2E6] text-[#893F82] text-[12px] font-semibold">{fields.length}</span>
              </div>
              <button onClick={onAddField} className={`${SX.btn} ${SX.btnSoft}`}>
                <Plus size={14} /> Add Field
              </button>
            </div>
            {fields.length === 0 ? (
              <div className="text-center px-4 py-12 text-[#8A8A90] text-[13px] leading-[1.6]">
                No fields yet. Use <b>Add Field</b> above to define the template schema.
              </div>
            ) : (
              fields.map((f, i) => (
                <FieldEditor
                  key={f.field_key}
                  index={i}
                  field={{ ...f, __history: buildFieldHistory(f, template.refinement_history, template.origin) }}
                  defaultOpen={true}
                  context={template}
                  onRename={onRename}
                  onChangeType={onChangeType}
                  onChangeInstruction={onChangeInstruction}
                  onChangeSchema={onChangeSchema}
                  onDelete={onDelete}
                  onToggleEnable={onToggleEnable}
                />
              ))
            )}
          </div>
          <div className={`${TX.helper} mt-3`}>
            {fields.filter((f) => f.enabled !== false).length} of {fields.length} field{fields.length === 1 ? "" : "s"} enabled — uncheck any you don't need before saving.
          </div>

          {/* Refinement log. The prototype has no History screen, but refinement_history
              is real backend data, so it lives here as a collapsed section. */}
          {rounds.length > 0 && (
            <div className="mt-[22px]">
              <button onClick={() => setHistoryOpen((o) => !o)}
                className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#52525B] hover:text-[#1A1A1A]">
                {historyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <Clock size={14} /> Refinement history ({rounds.length})
              </button>
              {historyOpen && (
                <div className="flex flex-col gap-2.5 mt-3">
                  {rounds.map((r, i) => {
                    const updated = r.fields_updated || [];
                    const scope = updated.length === 0 ? "All fields" : updated.length > 3 ? `${updated.length} fields` : updated.join(", ");
                    return (
                      <div key={i} className={`${SX.cardSm} px-4 py-[13px]`}>
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className={`${SX.badge} ${updated.length === 0 ? "bg-[#F7EBF5] text-[#893F82]" : "bg-[#F5F5F5] text-[#52525B] font-mono"}`}>{scope}</span>
                          {r.intent && <span className={`${SX.badge} bg-[#EEF4FD] text-[#2F5FA8]`}>{String(r.intent).replace(/_/g, " ")}</span>}
                          {r.created_at && <span className="text-[11.5px] text-[#8A8A90] ml-auto">{new Date(r.created_at).toLocaleString()}</span>}
                        </div>
                        <div className="text-[13.5px] font-medium text-[#1A1A1A] leading-[1.5]">{r.feedback}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateDetail;
